import {useCallback, useRef} from 'react';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {defaultFieldsFrom, defaultKpisFor} from './informeModel';

/**
 * El tipo de negocio del perfil del cliente (`ECOMMERCE`, `LEAD_GEN`…), o `null` si no lo tiene o
 * no se pudo leer: con `null` el informe nace con la semilla genérica, así que un error acá nunca
 * deja la pantalla sin informe.
 */
export function fetchBusinessType(clientId) {
    return new APIClient(`${API_URL}client/${clientId}/profile`).call('GET', {}, {cache: true})
        .then((r) => (r && r.status === 'OK' && r.data ? r.data.business_type || null : null))
        .catch(() => null);
}

function isBlank(template) {
    return !!template && !(template.fields || []).length && !(template.panels || []).length;
}

/**
 * Siembra un informe nuevo según el tipo de negocio del cliente (`seedFor` en el modelo).
 *
 * El perfil llega por red, y lo que pasa MIENTRAS era el defecto (review C-INFCONV-02): el informe
 * anterior seguía en pantalla, editable y con su id, con la URL ya de uno nuevo. Guardar escribía
 * sobre el viejo, y lo que se editara lo borraba la siembra al llegar. Por eso:
 *
 * 1. `seed()` deja **en el acto** una hoja en blanco (`informe: null`): no hay id viejo que guardar.
 * 2. Al llegar el perfil, siembra **sólo si la hoja sigue en blanco**. Si el usuario ya agregó algo,
 *    esa hoja es suya y no se pisa.
 * 3. `cancel()` descarta la siembra pendiente: lo llama abrir un informe, para que un perfil que
 *    llega tarde no reemplace el informe recién abierto por uno en blanco.
 *
 * Los `get*` son funciones y no valores porque se leen al RESOLVER, no al pedir.
 */
export function useSeedNewInforme({actions, getCatalog, getSourceType, getClientId, getTemplate,
                                   fetchType = fetchBusinessType}) {
    const requestRef = useRef(0);

    const cancel = useCallback(() => { requestRef.current += 1; }, []);

    const seed = useCallback(() => {
        requestRef.current += 1;
        const request = requestRef.current;
        const sow = (businessType) => {
            if (requestRef.current !== request) return;
            if (!isBlank(getTemplate())) return;
            const catalog = getCatalog();
            actions.newInforme(defaultFieldsFrom(catalog, getSourceType(), businessType), catalog,
                               defaultKpisFor(businessType));
        };
        const clientId = getClientId();
        if (!clientId) { sow(null); return; }
        actions.newInforme([], getCatalog());
        fetchType(clientId).then(sow);
        // Los `get*` leen refs: no cambian entre renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actions]);

    return {seed, cancel};
}
